import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { Lock, User, Eye, EyeOff, Shield } from 'lucide-react'
import { auth } from '@lib/firebase'

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Real Firebase Auth. This previously compared against a username and
    // password written into the source, which shipped in the public bundle and
    // so protected nothing; the Firestore rules now gate admin writes on a
    // verified signed-in email, which requires an actual session.
    try {
      await signInWithEmailAndPassword(auth, credentials.username.trim(), credentials.password)
      localStorage.setItem('adminAuth', 'true')
      navigate('/admin/dashboard')
    } catch (err) {
      const code = err?.code || ''
      if (code === 'auth/invalid-email') {
        setError('Enter the full admin email address.')
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Wait a few minutes and try again.')
      } else if (code === 'auth/network-request-failed') {
        setError('Network problem. Check your connection and try again.')
      } else {
        setError('Invalid email or password.')
      }
      localStorage.removeItem('adminAuth')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 pattern-dots opacity-10"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative"
      >
        <div className="glass-effect-dark rounded-xl p-8 premium-glow border border-primary-500/20">
          <div className="text-center mb-8">
            <img 
              src="/TRP_logo_compressed.png" 
              alt="Total Rewards Pro Logo" 
              className="w-20 h-12 object-contain mx-auto mb-4"
            />
            <h1 className="text-heading-1 text-white mb-2">Admin Login</h1>
            <p className="text-body text-neutral-300">Access the Total Rewards Pro admin dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Email
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={credentials.username}
                  onChange={handleChange}
                  required
                  className="form-input pl-12"
                  placeholder="you@totalrewardspro.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  required
                  className="form-input pl-12 pr-12"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-200"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-xl">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full justify-center"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>


        </div>
      </motion.div>
    </div>
  )
}

export default AdminLogin 